import { useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { ReportDateFilter, lastCompleteMonth, type Range } from "@/components/reports/report-date-filter";
import {
  money, ReportTable, Caveats, Finding,
  ReportCard, ReportNote, StatCard, AdminReportGate,
} from "@/components/reports/report-primitives";
import { BucketStageChart } from "@/components/reports/report-charts";

interface BucketRow {
  bucketKey: string; bucketLabel: string;
  orders: number; units: number;
  orderedCents: number; notInvoicedCents: number; invoicedCents: number;
  collectedCents: number; outstandingCents: number; grossBalanceCents: number;
  passThroughCents: number; cancelledOrders: number; cancelledCents: number;
  writtenOffCents: number;
  byStage: Record<string, number>;
}

export default function SalesByBucketPage() {
  usePageTitle("Sales by Bucket");
  const [range, setRange] = useState<Range>(lastCompleteMonth());

  const { data, isLoading, error } = useQuery<{ meta: any; rows: BucketRow[] }>({
    queryKey: ["/api/reports/sales-by-bucket", range.start, range.end],
    queryFn: async () => {
      const res = await fetch(`/api/reports/sales-by-bucket?start=${range.start}&end=${range.end}`,
        { credentials: "include" });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message ?? res.statusText);
      return res.json();
    },
  });

  const rows = data?.rows ?? [];
  const sum = (f: keyof BucketRow) => rows.reduce((a, r) => a + (r[f] as number), 0);
  const ordered = sum("orderedCents");
  const notInvoiced = sum("notInvoicedCents");
  const notInvoicedPct = ordered > 0 ? Math.round((notInvoiced / ordered) * 100) : 0;

  return (
    <AdminReportGate>
    <ResponsiveLayout
      title="Sales by Bucket"
      subtitle="Work orders created in the period, by bucket and current invoice stage"
      breadcrumbs={[{ label: "Reports", href: "/reports" }, { label: "Sales by Bucket" }]}
      backTo={{ label: "All reports", href: "/reports" }}
      isLoading={isLoading}
      actions={
        <Button variant="outline" size="sm" asChild>
          <a href={`/api/reports/sales-by-bucket.csv?start=${range.start}&end=${range.end}`}>
            <Download className="h-4 w-4 mr-2" />Export CSV
          </a>
        </Button>
      }
    >
      <div className="mb-6"><ReportDateFilter value={range} onChange={setRange} /></div>

      <ReportNote>
        Invoice and payment status reflect where these orders stand <strong>today</strong>, not what
        happened during the period. Dottie stores no invoice-issued or paid date, so “invoices sent”
        and “paid” within a period are not available. Service fees only — government fees are a
        pass-through and are shown separately.
      </ReportNote>

      {error && <p className="text-sm text-red-700">{(error as Error).message}</p>}

      {data && rows.length > 0 && (
        <>
          {notInvoiced > 0 && (
            <Finding action={
              <Button variant="outline" size="sm" asChild>
                <Link href="/reports/billing">Review uninvoiced orders</Link>
              </Button>
            }>
              <strong>{money(notInvoiced)} of this period’s work has not been invoiced</strong>
              {" "}— {notInvoicedPct}% of everything ordered.
            </Finding>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <StatCard label="Ordered service value" value={money(ordered)} sub={`${sum("orders")} orders`} />
            <StatCard label="Not invoiced" value={money(notInvoiced)} tone="text-gray-600" sub={`${notInvoicedPct}% of ordered`} />
            <StatCard label="Awaiting payment" value={money(sum("outstandingCents"))} tone="text-amber-600" />
            <StatCard label="Collected to date" value={money(sum("collectedCents"))} tone="text-emerald-700"
              sub="on work ordered in this period" />
          </div>

          <ReportCard title="Where the money is"
            description="Service dollars by invoice stage. Gross amounts are never segments here.">
            <BucketStageChart rows={rows} />
          </ReportCard>

          <ReportCard title="By bucket"
            description={`+ ${money(sum("passThroughCents"))} government fees passed through — never counted as sales. Gross unpaid balance: ${money(sum("grossBalanceCents"))}.`}>
            <ReportTable
              columns={[
                { key: "bucketLabel", header: "Bucket" },
                { key: "orders", header: "Orders", align: "right" },
                { key: "avg", header: "Avg / order", align: "right",
                  render: (r: BucketRow) => <span className="text-gray-500">{r.orders ? money(r.orderedCents / r.orders) : "—"}</span> },
                { key: "orderedCents", header: "Ordered", align: "right", render: (r: BucketRow) => money(r.orderedCents) },
                { key: "notInvoicedCents", header: "Not invoiced", align: "right",
                  render: (r: BucketRow) => <span className="text-gray-500">{money(r.notInvoicedCents)}</span> },
                { key: "outstandingCents", header: "Awaiting payment", align: "right",
                  render: (r: BucketRow) => <span className="text-amber-700">{money(r.outstandingCents)}</span> },
                { key: "collectedCents", header: "Collected", align: "right", render: (r: BucketRow) => money(r.collectedCents) },
                { key: "writtenOffCents", header: "Written off", align: "right",
                  render: (r: BucketRow) => r.writtenOffCents
                    ? <span className="text-gray-500">{money(r.writtenOffCents)}</span> : "—" },
                { key: "cancelled", header: "Cancelled", align: "right",
                  render: (r: BucketRow) => <span className="text-gray-500">{r.cancelledOrders} · {money(r.cancelledCents)}</span> },
              ]}
              rows={rows}
              footer={{
                bucketLabel: "Total", orders: sum("orders"),
                avg: sum("orders") ? money(ordered / sum("orders")) : "—",
                orderedCents: money(ordered), notInvoicedCents: money(notInvoiced),
                outstandingCents: money(sum("outstandingCents")),
                collectedCents: money(sum("collectedCents")),
                writtenOffCents: money(sum("writtenOffCents")),
                cancelled: `${sum("cancelledOrders")} · ${money(sum("cancelledCents"))}`,
              }}
            />
          </ReportCard>

          <Caveats items={data.meta?.caveats ?? []} />
        </>
      )}
    </ResponsiveLayout>
    </AdminReportGate>
  );
}
